<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="pageTitle">
          <template v-slot:toolbar>
            <div class="row">
              <div class="ml-3">
                <button
                  type="button"
                  class="btn btn-primary font-weight-bolder btn-sm"
                  @click="addNew"
                >
                  <i style="font-size: 1rem" class="flaticon2-add-1"></i>Tạo mới
                </button>
              </div>
            </div>
          </template>
          <template v-slot:preview>
            <!-- Bộ lọc -->
            <div class="col-md-12 mb-5 pl-0">
              <b-row>
                <b-col md="3" class="pl-0">
                  <b-input
                    size="sm"
                    placeholder="Tìm theo tên chương trình"
                    v-model="filter.name"
                    autocomplete="off"
                    v-on:input="debounceSearch"
                  ></b-input>
                </b-col>
                <b-col md="2">
                  <b-form-select
                    size="sm"
                    v-model="filter.status"
                    :options="statusOptions"
                    value-field="id"
                    text-field="name"
                    @change="onFilter"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null"
                        >-- Tất cả trạng thái --</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-col>
                <b-col md="2">
                  <date-picker
                    placeholder="Từ ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.fromDate"
                  ></date-picker>
                </b-col>
                <b-col md="2">
                  <date-picker
                    placeholder="Đến ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.toDate"
                  ></date-picker>
                </b-col>
                <b-col md="1">
                  <b-button
                    style="width: 70px"
                    variant="primary"
                    size="sm"
                    @click="onFilter"
                    >Lọc</b-button
                  >
                </b-col>
              </b-row>
            </div>

            <!-- Bảng dữ liệu -->
            <b-table
              :items="items"
              :fields="fields"
              bordered
              hover
              :busy="onLoading"
              show-empty
              empty-text="Không có chương trình nào"
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="8"></vcl-table>
              </template>
              <template v-slot:cell(startDate)="row">
                <span>{{ formatShortDate(row.item.startDate) }}</span>
              </template>
              <template v-slot:cell(endDate)="row">
                <span v-if="row.item.endDate">{{
                  formatShortDate(row.item.endDate)
                }}</span>
                <span v-else class="text-muted">--</span>
              </template>
              <template v-slot:cell(status)="row">
                <span
                  class="label font-weight-bold label-lg label-inline"
                  :class="statusVariant(row.item.status)"
                  >{{ statusName(row.item.status) }}</span
                >
              </template>
              <template v-slot:cell(createdAt)="row">
                <p class="text-center mb-0">
                  {{ formatDateTime(row.item.createdAt) }}
                </p>
                <p class="text-center mb-0">{{ row.item.createdByName }}</p>
              </template>
              <template v-slot:cell(actions)="row">
                <div class="d-flex justify-content-center">
                  <b-dropdown size="sm" no-caret right>
                    <template slot="button-content">
                      <i
                        style="font-size: 1rem; padding-right: 0px"
                        class="flaticon2-settings"
                      ></i>
                    </template>
                    <b-dropdown-item @click="editItem(row.item)">
                      <span style="color: #3f4254; font-size: 12px">
                        <i style="font-size: 1rem" class="flaticon2-pen"></i>
                        &nbsp; Chỉnh sửa
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="duplicateItem(row.item)">
                      <span style="color: #3f4254; font-size: 12px">
                        <i style="font-size: 1rem" class="far fa-copy"></i>
                        &nbsp; Sao chép
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="showStatusModal(row.item)">
                      <span style="color: #3f4254; font-size: 12px">
                        <i
                          style="font-size: 1rem"
                          class="fas fa-exchange-alt"
                        ></i>
                        &nbsp; Đổi trạng thái
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="showHistory(row.item)">
                      <span style="color: #3f4254; font-size: 12px">
                        <i style="font-size: 1rem" class="fas fa-history"></i>
                        &nbsp; Xem lịch sử
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-item
                      @click="showDeleteAlert(row.item)"
                      v-if="checkPermission('REWARD_DELETE')"
                    >
                      <span style="color: #3f4254; font-size: 12px">
                        <i
                          style="font-size: 1rem; color: #d33"
                          class="flaticon2-rubbish-bin-delete-button"
                        ></i>
                        &nbsp; Xóa
                      </span>
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>
            </b-table>

            <!-- Phân trang -->
            <b-row>
              <b-col>
                <p class="mt-3 text-dark" style="font-weight: 500">
                  Tổng số chương trình: {{ totalItems }}
                </p>
              </b-col>
              <b-col>
                <b-pagination-nav
                  class="custom-pagination"
                  v-show="totalPages >= 2"
                  :link-gen="linkGen"
                  :number-of-pages="totalPages"
                  use-router
                  @change="fetchData"
                  align="right"
                  first-class="page-item-first btn btn-icon btn-sm mr-1 my-1"
                  prev-class="page-item-prev btn btn-icon btn-sm mr-.5 my-1"
                  next-class="page-item-next btn btn-icon btn-sm mr-1 my-1 ml-.5"
                  last-class="page-item-last btn btn-icon btn-sm my-1"
                  page-class="btn btn-icon btn-sm border-0 mr-.5 my-1"
                >
                  <template v-slot:first-text>
                    <span>
                      <i class="ki ki-bold-double-arrow-back icon-xs"></i>
                    </span>
                  </template>
                  <template v-slot:prev-text>
                    <i class="ki ki-bold-arrow-back icon-xs"></i>
                  </template>
                  <template v-slot:next-text>
                    <i class="ki ki-bold-arrow-next icon-xs"></i>
                  </template>
                  <template v-slot:last-text>
                    <span class="text-info">
                      <i class="ki ki-bold-double-arrow-next icon-xs"></i>
                    </span>
                  </template>
                </b-pagination-nav>
              </b-col>
            </b-row>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- Modal đổi trạng thái -->
    <b-modal ref="status-modal" hide-footer title="Đổi trạng thái chương trình">
      <p>
        Chương trình: <b>{{ statusModel.name }}</b>
      </p>
      <b-form-group>
        <label>Trạng thái mới</label>
        <b-form-select
          size="sm"
          v-model="statusModel.status"
          :options="allowedStatusOptions"
          value-field="id"
          text-field="name"
        ></b-form-select>
      </b-form-group>
      <b-alert show variant="warning" class="mb-3">
        Chương trình ở trạng thái <b>Hoạt động</b> mới tham gia tính hoa hồng.
        Sau khi kích hoạt thì không quay lại trạng thái Nháp được.
      </b-alert>
      <b-button
        variant="primary"
        size="sm"
        style="width: 70px"
        :disabled="isUpdatingStatus"
        @click="updateStatus"
        >Lưu</b-button
      >
      <b-button
        variant="secondary"
        size="sm"
        style="margin-left: 10px; width: 70px"
        @click="hideStatusModal"
        >Hủy</b-button
      >
    </b-modal>

    <HistoryModal
      ref="history-modal"
      modal-id="commission-program-history-modal"
      title="Lịch sử thay đổi chương trình hoa hồng"
    />
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import Swal from 'sweetalert2';
import decounce from 'debounce';
import moment from 'moment';
import datePicker from 'vue-bootstrap-datetimepicker';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import { VclTable } from 'vue-content-loading';
import localData from '@/utils/saveDataToLocal';
import { TIME_TRIGGER } from '@/utils/constants';
import { formatDate, makeToastSuccess, makeToastFaile } from '@/utils/common';
import HistoryModal from './HistoryModal.vue';
import {
  COMMISSION_PROGRAM_TYPE,
  COMMISSION_PROGRAM_TYPE_NAME,
  COMMISSION_PROGRAM_STATUS,
  COMMISSION_PROGRAM_STATUS_NAME,
  COMMISSION_PROGRAM_STATUS_VARIANT,
  toOptionList,
} from './commission.constants';
import commissionReportMixin from './commissionReport.mixin';

const PAGE_LIMIT = 10;
const DATE_INPUT_FORMAT = 'DD/MM/YYYY';
const DATE_API_FORMAT = 'YYYY-MM-DD';

export default {
  components: {
    KTCodePreview,
    VclTable,
    HistoryModal,
    datePicker,
  },
  mixins: [commissionReportMixin],
  data() {
    return {
      onLoading: false,
      isUpdatingStatus: false,
      items: [],
      totalPages: 0,
      totalItems: 0,
      page: 1,
      programType: COMMISSION_PROGRAM_TYPE.STORE,
      filter: {
        name: '',
        status: null,
        fromDate: '',
        toDate: '',
      },
      statusModel: {
        id: null,
        name: '',
        status: COMMISSION_PROGRAM_STATUS.ACTIVE,
        currentStatus: COMMISSION_PROGRAM_STATUS.ACTIVE,
      },
      statusOptions: toOptionList(COMMISSION_PROGRAM_STATUS_NAME),
      linkGen: (pageNum) => (pageNum === 1 ? '?' : `?page=${pageNum}`),
      fields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
          tdClass: 'text-center',
        },
        {
          key: 'name',
          label: 'Tên chương trình',
          thStyle: { fontWeight: 600, color: '#181c32', width: '22%' },
        },
        {
          key: 'description',
          label: 'Mô tả',
          thStyle: { fontWeight: 600, color: '#181c32', width: '23%' },
        },
        {
          key: 'startDate',
          label: 'Từ ngày',
          thStyle: { fontWeight: 600, color: '#181c32', width: '9%' },
          tdClass: 'text-center',
        },
        {
          key: 'endDate',
          label: 'Đến ngày',
          thStyle: { fontWeight: 600, color: '#181c32', width: '9%' },
          tdClass: 'text-center',
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: { fontWeight: 600, color: '#181c32', width: '11%' },
          tdClass: 'text-center',
        },
        {
          key: 'createdAt',
          label: 'Ngày tạo / Người tạo',
          thStyle: { fontWeight: 600, color: '#181c32', width: '16%' },
        },
        {
          key: 'actions',
          label: '',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
        },
      ],
    };
  },
  computed: {
    pageTitle() {
      return COMMISSION_PROGRAM_TYPE_NAME[this.programType] || 'Chương trình hoa hồng';
    },
    /** Chỉ 2 trạng thái, chuyển qua lại tự do */
    allowedStatusOptions() {
      return this.statusOptions;
    },
  },
  watch: {
    '$route.query.type': function () {
      this.resolveProgramType();
      this.setBreadcrumb();
      this.fetchData();
    },
  },
  created() {
    this.resolveProgramType();
    this.setBreadcrumb();
    this.fetchData();
  },
  methods: {
    resolveProgramType() {
      const type = Number(this.$route.query.type);
      this.programType =
        type === COMMISSION_PROGRAM_TYPE.CR
          ? COMMISSION_PROGRAM_TYPE.CR
          : COMMISSION_PROGRAM_TYPE.STORE;
    },
    setBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Hoa hồng V2' },
        { title: this.pageTitle },
      ]);
    },
    checkPermission(condition) {
      return localData.checkPermission(condition);
    },
    statusName(status) {
      return COMMISSION_PROGRAM_STATUS_NAME[status] || '';
    },
    statusVariant(status) {
      return COMMISSION_PROGRAM_STATUS_VARIANT[status] || 'label-light-info';
    },
    formatShortDate(value) {
      return value ? formatDate(value, DATE_INPUT_FORMAT) : '';
    },
    formatDateTime(value) {
      return value ? formatDate(value, 'DD/MM/YYYY HH:mm') : '';
    },
    toApiDate(value) {
      if (!value) {
        return undefined;
      }
      const parsed = moment(value, DATE_INPUT_FORMAT, true);
      return parsed.isValid() ? parsed.format(DATE_API_FORMAT) : undefined;
    },
    debounceSearch: decounce(function () {
      this.onFilter();
    }, TIME_TRIGGER),
    onFilter() {
      if (this.$route.query.page !== undefined) {
        this.$router
          .replace({ query: { ...this.$route.query, page: undefined } })
          .catch(() => {});
      }
      this.fetchData();
    },
    fetchData() {
      this.onLoading = true;
      this.page = Number(this.$route.query.page) || 1;

      const params = {
        type: this.programType, // BE bắt buộc truyền type
        page: this.page,
        limit: PAGE_LIMIT,
        name: this.filter.name || undefined,
        status: this.filter.status || undefined,
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      };

      ApiService.setHeader();
      ApiService.query('commission-programs', { params })
        .then(({ data }) => {
          const result = data.data || {};
          const list = result.data || [];
          this.totalPages = result.totalPage || result.total_page || 0;
          this.totalItems = result.totalRow || result.total_row || 0;
          const offset = (this.page - 1) * PAGE_LIMIT;
          this.items = list.map((item, index) => ({
            ...item,
            count: offset + index + 1,
          }));
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được danh sách chương trình!'),
          );
        })
        .finally(() => {
          this.onLoading = false;
        });
    },
    addNew() {
      this.$router.push({
        name: 'commission-upsert-program',
        query: { type: this.programType },
      });
    },
    editItem(item) {
      this.$router.push({
        name: 'commission-upsert-program',
        query: { id: item.id, type: this.programType },
      });
    },
    showHistory(item) {
      this.$refs['history-modal'].show(
        `commission-programs/${item.id}/histories`,
      );
    },
    /**
     * Sao chép = MỞ TRANG TẠO với dữ liệu của chương trình nguồn.
     * Người dùng sửa lại (tên, kỳ, cửa hàng...) rồi mới bấm Tạo.
     *
     * Trước đây gọi thẳng API /duplicate nên tạo ngay một bản ghi ở trạng thái
     * Nháp — vừa đẻ rác vừa không sửa được trước khi tạo.
     */
    duplicateItem(item) {
      this.$router.push({
        name: 'commission-upsert-program',
        query: { copyFrom: item.id, type: this.programType },
      });
    },
    showStatusModal(item) {
      this.statusModel = {
        id: item.id,
        name: item.name,
        status: item.status,
        currentStatus: item.status,
      };
      this.$refs['status-modal'].show();
    },
    hideStatusModal() {
      this.$refs['status-modal'].hide();
    },
    updateStatus() {
      this.isUpdatingStatus = true;
      ApiService.setHeader();
      ApiService.put(`commission-programs/${this.statusModel.id}/status`, {
        status: this.statusModel.status,
      })
        .then(({ data }) => {
          makeToastSuccess(data.message || 'Đổi trạng thái thành công!');
          this.hideStatusModal();
          this.fetchData();
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Đổi trạng thái thất bại!'),
          );
        })
        .finally(() => {
          this.isUpdatingStatus = false;
        });
    },
    showDeleteAlert(item) {
      Swal.fire({
        title: 'Xóa chương trình hoa hồng',
        text: `Bạn có chắc muốn xóa chương trình "${item.name}" không?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        position: 'top',
      }).then((result) => {
        if (result.value) {
          this.deleteItem(item);
        }
      });
    },
    deleteItem(item) {
      ApiService.setHeader();
      ApiService.delete(`commission-programs/${item.id}`).then((response) => {
        // ApiService.delete đã tự toast lỗi và trả về đối tượng error khi thất bại
        if (!response || !response.data) {
          return;
        }
        makeToastSuccess(
          response.data.message || 'Xóa chương trình hoa hồng thành công!',
        );
        this.fetchData();
      });
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
  border-top: 1px solid #ecf0f3;
}
</style>
